/**
 * The ⓘ — one sentence of explanation, behind a control, instead of a paragraph standing on the
 * surface.
 *
 * ── Why it exists ────────────────────────────────────────────────────────────────────────────
 * The owner's standing rule, carried over from the mobile app's progression round: **an explanation
 * lives under an ⓘ or it goes.** The day panel had two paragraphs sitting permanently under two
 * fields — what the day's rest restamps, and why a progression rule cannot be edited yet — and the
 * programator mock draws neither. Both are real information a trainer occasionally needs and neither
 * is worth the vertical space on every card of every day.
 *
 * Promoted to `shared/ui` on its SECOND consumer, which is the rule: `plan-day-programming`'s day
 * rest and `plan-progression`'s foot are two slices, so neither could own it.
 *
 * ── It is a Popover, not a Tooltip, and that is an accessibility decision ────────────────────
 * A tooltip opens on hover. A touch user has no hover and a keyboard user who never leaves the home
 * row has none either, which is exactly the correction "+ Faza" took on 4 August 2026 — an
 * explanation nobody but a screen-reader user ever met. This opens on a press, from a real button
 * with a real accessible name, and dismisses on Escape and on a click outside.
 *
 * Base UI supplies the behaviour — positioning, dismissal, focus, the ARIA wiring between the
 * trigger and the popup — and every visual byte is ours. `.positioner` carries the `z-index` rather
 * than `.popup`, because Base UI positions through inline styles on the positioner and the popup
 * inside it is `position: static`; a `z-index` there applies to nothing, which is how the session
 * card's menu once ended up visible and unclickable (`MenuSurface.module.css` records it).
 *
 * The surface IS `MenuSurface`'s — worn, not transcribed. This app has one popup, so the ⓘ's
 * carries that module's class and adds only its own three geometry values; the stylesheet beside
 * this file says why the class is doubled and what the transcription used to drop.
 */

import { Popover } from '@base-ui/react/popover';

import menuStyles from './MenuSurface.module.css';
import styles from './HintPopover.module.css';

/** One option the popover names, and what it means. */
export interface HintTerm {
  readonly term: string;
  readonly meaning: string;
}

export interface HintPopoverProps {
  /**
   * The trigger's accessible name — a QUESTION, never "info".
   *
   * The glyph is `aria-hidden`, so this is the whole of what a screen reader hears before the popup
   * opens; "więcej informacji" repeated four times on one panel names nothing.
   */
  readonly label: string;
  /** The sentence itself. One paragraph — a popover holding a section is a dialog. */
  readonly children: string;
  /**
   * A control's options, one line each, above the sentence — a legend for a switch, not a
   * section. The sentence then carries what holds for all of them.
   */
  readonly terms?: readonly HintTerm[];
  /**
   * `question` is the „?" the owner asked for beside a chart's switch (24 September 2026): the
   * trigger explains how to READ the control next to it rather than adding a fact about it.
   */
  readonly glyph?: 'info' | 'question';
}

export function HintPopover({ label, children, terms, glyph = 'info' }: HintPopoverProps) {
  return (
    <Popover.Root>
      <Popover.Trigger
        className={`${String(styles.trigger)} ${glyph === 'question' ? String(styles.question) : ''}`}
        aria-label={label}
      >
        {/* U+24D8 rather than an SVG: it is one glyph at one size in one weight, and the app's
          * icon set draws nothing like it. Hidden from the accessibility tree because the button's
          * own name says what it opens. */}
        <span aria-hidden="true">{glyph === 'question' ? '?' : 'ⓘ'}</span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner className={styles.positioner} sideOffset={6} align="end">
          <Popover.Popup className={`${String(menuStyles.popup)} ${String(styles.popup)}`}>
            <Popover.Title className={styles.title}>{label}</Popover.Title>
            {terms === undefined ? null : (
              <dl className={styles.terms}>
                {terms.map((entry) => (
                  <div className={styles.termRow} key={entry.term}>
                    <dt className={styles.term}>{entry.term}</dt>
                    <dd className={styles.meaning}>{entry.meaning}</dd>
                  </div>
                ))}
              </dl>
            )}
            <p className={terms === undefined ? styles.body : `${String(styles.body)} ${String(styles.footnote)}`}>
              {children}
            </p>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
